import React, { useContext, useMemo } from 'react';
import Button from 'components/common/button';
import DownloadIcon from 'assets/svg/download';
import { MultiMediaResponseDTO } from 'artifacts/dtos';
import { useGetPatientMultimediaByMultiMediaId } from 'hooks/useApi';
import Spinner from 'components/spinner/Spinner';
import { downloadFromUrl } from 'utils/files';
import { isEmptyString } from 'utils/helper';
import { MultiMediaResponse } from 'artifacts/dtos/multiMediaResponse';
import head from 'lodash/head';
import { NotificationContext } from 'contexts/notification';
import { AlertVariant } from 'components/common/Alerts';
import useIncompleteTempoRedirect from 'hooks/useIncompleteTempoRedirect';
import clsx from 'clsx';

type Props = {
  signedMultimedia: MultiMediaResponseDTO;
}

type RowProps = {
  media: MultiMediaResponse;
}

function SignedConsentFileRow({ media }: RowProps) {
  const { notify } = useContext(NotificationContext);
  const {
    mutateAsync: getPatientMultimediaByMultiMediaId,
    isPending: isDownloading,
  } = useGetPatientMultimediaByMultiMediaId();

  const handleDownload = async () => {
    const { mediaId, fileName: mediaFileName } = media;
    try {
      const data = await getPatientMultimediaByMultiMediaId(mediaId ?? 0);
      const fileUrl = head(data?.list)?.url?.value ?? '';
      const fileName = mediaFileName ?? head(data?.list)?.fileName ?? 'download';

      if (isEmptyString(fileUrl)) {
        notify({
          message: 'Unable to download this file right now. Please try again.',
          variant: AlertVariant.ERROR,
        });
      } else {
        await downloadFromUrl(fileUrl, fileName);
      }
    } catch (e) {
      console.error('Download failed:', e);
      notify({
        message: 'Download failed. Please try again.',
        variant: AlertVariant.ERROR,
      });
    }
  };

  return (
    <div key={media.mediaId} className="flex items-center justify-between gap-4 border-b border-gray-300 py-2">
      <div className="w-full text-gray-500">
        {media.fileName ?? 'Unknown file'}
      </div>
      <Button
        variant="text"
        color="inherit"
        className="rounded !min-w-fit"
        classWrap="!w-14"
        disabled={isDownloading || !media.mediaId}
        onClick={handleDownload}
      >
        {isDownloading ? (
          <span className="!w-4 !h-4 flex items-center justify-center">
            <Spinner
              name="MoonLoader"
              size={16}
              color="#0d968b"
            />
          </span>
        ) : (
          <DownloadIcon className="!w-4 !h-4" />
        )}
      </Button>
    </div>
  );
}

export default function SignedConsentFiles({ signedMultimedia }: Props) {
  const medias = useMemo(() => signedMultimedia?.list ?? [], [signedMultimedia?.list]);
  const { isTablet } = useIncompleteTempoRedirect(true);

  return isTablet ? (
    <div className="app-page-container flex min-h-screen items-center justify-center">
      <Spinner />
    </div>
  ) : (
    <div className={clsx('app-page-container p-4 flex flex-col')}>
      <div className="flex flex-col gap-4 text-gray-600 my-2">
        <span className="text-xl font-semibold">Forms completed successfully.</span>
        <span>
          You can download the completed forms below:
        </span>
      </div>
      <div className="border-t border-dashed my-2">
        {medias.map((media) => (
          <SignedConsentFileRow
            key={media.mediaId}
            media={media}
          />
        ))}
      </div>
    </div>
  );
}
